import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../store";
import { ApplicationError } from "../../../../models/errors/application-error";
import { ApplicationBasket } from "../../../../models/order/basket/application-basket";
import { ApplicationBasketItemList } from "../../../../models/order/basket/application-basket-item-list";
import { ApplicationBasketTotals } from "../../../../models/order/basket/application-basket-totals";
import BasketService from "../../../../services/order/basket-service";
import { ApplicationBasketItem } from "../../../../models/order/basket/application-basket-item";
import { showSuccess } from "../../../../components/notification/toastr-actions";
import { mergeArraysByField } from "../../../../helpers/array-helper";
import { ApplicationApprovalStatus } from "../../../../models/order/application-approval-status";
import { FetchMoreData, FetchParams } from "../../../../components/controls/table-view";

interface BasketState {
    basket: ApplicationBasket | null
    basketItems: ApplicationBasketItemList | null
    basketTotals: ApplicationBasketTotals | null
    basketTotalsLoading: boolean
    basketTotalsError: string | null
}

const initialState: BasketState = {
    basket: null,
    basketItems: {
        items: [],
        count: -1,
        currentPosition: 0
    },
    basketTotals: null,
    basketTotalsLoading: false,
    basketTotalsError: null,
};

interface UpdateBasketItemResponse {
    basketItemId: number
    value: number | string
}

export const fetchBasket = createAsyncThunk<ApplicationBasket, number, { state: RootState }>(
    'basket/fetch',
    async (branchId, { rejectWithValue }) => {
        const basketService = new BasketService();
        try {
            return await basketService.getUserBasket(branchId);
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const fetchBasketItems = createAsyncThunk<ApplicationBasketItemList | null, { basketId: number, fetchParams: FetchParams }, { state: RootState }>(
    'basketItems/fetch',
    async ({ basketId, fetchParams }, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const basketService = new BasketService();
        try {
            var currentPosition: number = state.basket.basketItems?.currentPosition ?? 0;
            var fetchNext: number = 15;
            var count: number = state.basket.basketItems?.count ?? -1;

            if (fetchParams.reset) {
                currentPosition = 0;
                count = -1;
                dispatch(resetBasketItems())
            }

            if (currentPosition != count) {
                return await basketService.getBasketItems(basketId, currentPosition, fetchNext, fetchParams.searchText ?? "")
            }
            return null;
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const fetchBasketTotals = createAsyncThunk<ApplicationBasketTotals, number, { state: RootState }>(
    'basket/totals',
    async (basketId, { rejectWithValue }) => {
        const basketService = new BasketService();
        try {
            return await basketService.getBasketTotals(basketId);
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const fetchByProductId = createAsyncThunk<ApplicationBasketItem | null, { basketId: number, productId: number }, { state: RootState }>(
    'basketItems/product/fetch',
    async ({ basketId, productId }, { getState, rejectWithValue }) => {
        const state = getState();
        const basketService = new BasketService();
        try {
            return await basketService.getByProductId(basketId, productId)
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const insertBasketItem = createAsyncThunk<
    ApplicationBasketItem,
    { basketId: number, basketItem: ApplicationBasketItem },
    { state: RootState }>(
        'basketItems/insert',
        async ({ basketId, basketItem }, { dispatch, rejectWithValue }) => {
            const basketService = new BasketService();
            try {
                basketItem.id = await basketService.insertBasketItem(basketId, basketItem)
                dispatch(fetchBasketTotals(basketId));
                return basketItem;
            } catch (error: any) {
                const apiError = ApplicationError.handleApiError(error, {
                    422: "Your basket has expired and been refreshed. Please try adding your item again."
                });
                return rejectWithValue(apiError);
            }
        }
    );

export const updateBasketItemStock = createAsyncThunk<
    UpdateBasketItemResponse,
    { basketItemId: number, currentStock: number },
    { state: RootState }>(
        'basketItems/updateStock',
        async ({ basketItemId, currentStock }, { dispatch, getState, rejectWithValue }) => {
            const basketService = new BasketService();
            try {

                const state = getState().basket;
                var basketItem = state.basketItems?.items.find(x => x.id == basketItemId);

                if (!basketItem) {
                    throw new ApplicationError("No basket item found");
                }

                await basketService.updateBasketItem(basketItemId, basketItem.quantity, currentStock, basketItem.notes)
                return { basketItemId: basketItemId, value: currentStock };
            } catch (error: any) {
                const apiError = ApplicationError.handleApiError(error, {});
                return rejectWithValue(apiError);
            }
        }
    );

export const updateBasketItemQuantity = createAsyncThunk<
    UpdateBasketItemResponse,
    { basketItemId: number, quantity: number },
    { state: RootState }>(
        'basketItems/updateQuantity',
        async ({ basketItemId, quantity }, { dispatch, getState, rejectWithValue }) => {
            const basketService = new BasketService();
            try {

                const state = getState().basket;
                var basketItem = state.basketItems?.items.find(x => x.id == basketItemId);
                if (!basketItem) {
                    throw new ApplicationError("No basket item found");
                }

                await basketService.updateBasketItem(basketItemId, quantity, basketItem.currentStock, basketItem.notes)
                dispatch(fetchBasketTotals(basketItem.basketId));
                return { basketItemId: basketItemId, value: quantity };
            } catch (error: any) {
                const apiError = ApplicationError.handleApiError(error, {});
                return rejectWithValue(apiError);
            }
        }
    );

export const updateBasketItemNotes = createAsyncThunk<
    UpdateBasketItemResponse,
    { basketItemId: number, notes: string },
    { state: RootState }>(
        'basketItems/updateNotes',
        async ({ basketItemId, notes }, { dispatch, getState, rejectWithValue }) => {
            const basketService = new BasketService();
            try {

                const state = getState().basket;
                var basketItem = state.basketItems?.items.find(x => x.id == basketItemId);
                if (!basketItem) {
                    throw new ApplicationError("No basket item found");
                }

                await basketService.updateBasketItem(basketItemId, basketItem.quantity, basketItem.currentStock, notes)
                dispatch(fetchBasketTotals(basketItem.basketId));
                return { basketItemId: basketItemId, value: notes };
            } catch (error: any) {
                const apiError = ApplicationError.handleApiError(error, {});
                return rejectWithValue(apiError);
            }
        }
    );

export const updateBasketItem = createAsyncThunk<
    void,
    { basketItemId: number, quantity: number, currentStock: number, notes: string },
    { state: RootState }>(
        'basketItems/updateBasketItem',
        async ({ basketItemId, quantity, currentStock, notes }, { dispatch, getState, rejectWithValue }) => {
            const basketService = new BasketService();
            try {
                const basketItem = getState().basket.basketItems?.items.find(x => x.id == basketItemId);
                if (!basketItem) {
                    throw new ApplicationError("No basket item found");
                }
                await basketService.updateBasketItem(basketItemId, quantity, currentStock, notes)
                dispatch(fetchBasketTotals(basketItem.basketId));
            } catch (error: any) {
                const apiError = ApplicationError.handleApiError(error, {});
                return rejectWithValue(apiError);
            }
        }
    );

export const deleteBasketItem = createAsyncThunk<
    number,
    number,
    { state: RootState }>(
        'basketItems/remove',
        async (basketItemId, { dispatch, getState, rejectWithValue }) => {
            const basketService = new BasketService();
            try {

                const state = getState().basket;
                var basketItem = state.basketItems?.items.find(x => x.id == basketItemId);

                if (!basketItem) {
                    throw new ApplicationError("No basket item found");
                }

                await basketService.deleteBasketItem(basketItemId);
                dispatch(showSuccess(`${basketItem.productName} removed from basket`));
                dispatch(fetchBasketTotals(basketItem.basketId));

                return basketItemId;
            } catch (error: any) {
                const apiError = ApplicationError.handleApiError(error, {});
                return rejectWithValue(apiError);
            }
        }
    );


const basketSlice = createSlice({
    name: 'basket',
    initialState,
    reducers: {
        resetBasket: (state) => {
            state.basket = initialState.basket;
            state.basketItems = initialState.basketItems;
            state.basketTotals = initialState.basketTotals;
            state.basketTotalsLoading = initialState.basketTotalsLoading;
            state.basketTotalsError = initialState.basketTotalsError;
        },
        resetBasketItems: (state) => {
            state.basketItems = initialState.basketItems;
        },
        resetBasketTotals: (state) => {
            state.basketTotals = initialState.basketTotals;
            state.basketTotalsLoading = initialState.basketTotalsLoading;
            state.basketTotalsError = initialState.basketTotalsError;
        },
        updateApprovalStatus: (state, action: PayloadAction<{ basketItemId: number; approvalStatus: ApplicationApprovalStatus }>) => {
            const { basketItemId, approvalStatus } = action.payload;
            updateBasketItemProperty(state, basketItemId, 'approvalStatus', approvalStatus);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBasket.fulfilled, (state, action: PayloadAction<ApplicationBasket>) => {
                state.basket = action.payload;
                state.basketItems = initialState.basketItems;
            })
            .addCase(fetchBasketTotals.pending, (state) => {
                state.basketTotalsLoading = true;
                state.basketTotalsError = null;
            })
            .addCase(fetchBasketTotals.fulfilled, (state, action: PayloadAction<ApplicationBasketTotals>) => {
                state.basketTotals = action.payload;
                state.basketTotalsLoading = false;
                state.basketTotalsError = null;
            })
            .addCase(fetchBasketTotals.rejected, (state, action) => {
                state.basketTotalsLoading = false;
                state.basketTotalsError = action.error.message ?? "Failed to fetch basket totals";
            })
            .addCase(fetchBasketItems.fulfilled, handleFetchBasketItemsFulfilled)
            .addCase(insertBasketItem.fulfilled, (state, action: PayloadAction<ApplicationBasketItem>) => {
                state.basketItems?.items.push(action.payload);
                state.basket!.itemCount += 1;
                state.basketItems!.count += state.basketItems!.count == -1 ? 2 : 1;
            })
            .addCase(insertBasketItem.rejected, (state) => {
                // Basket was stale/abandoned on the server — clear it so useBasket re-fetches a fresh one.
                state.basket = null;
                state.basketItems = initialState.basketItems;
            })
            .addCase(updateBasketItemStock.fulfilled, (state, action: PayloadAction<{ basketItemId: number; value: number | string }>) => {
                updateBasketItemProperty(state, action.payload.basketItemId, 'currentStock', action.payload.value as number);
            })
            .addCase(updateBasketItemQuantity.fulfilled, (state, action: PayloadAction<{ basketItemId: number; value: number | string }>) => {
                const quantity = action.payload.value as number;
                updateBasketItemProperty(state, action.payload.basketItemId, 'quantity', quantity);

                // Recalculated locally for immediate feedback; the API remains authoritative on next full fetch.
                const basketItem = state.basketItems?.items.find(item => item.id === action.payload.basketItemId);
                if (basketItem?.layerSize) {
                    basketItem.layersOrdered = quantity / basketItem.layerSize;
                    if (basketItem.fullPalletQuantity) {
                        basketItem.palletEquivalent = quantity / basketItem.fullPalletQuantity;
                    }
                }
            })
            .addCase(updateBasketItemNotes.fulfilled, (state, action: PayloadAction<{ basketItemId: number; value: number | string }>) => {
                updateBasketItemProperty(state, action.payload.basketItemId, 'notes', action.payload.value as string);
            })
            .addCase(deleteBasketItem.fulfilled, (state, action: PayloadAction<number>) => {
                if (state.basketItems) {
                    const index = state.basketItems.items.findIndex(item => item.id === action.payload);

                    if (index !== -1) {
                        state.basketItems.items.splice(index, 1);
                        state.basketItems.count -= 1;
                        state.basketItems.currentPosition -= 1;
                        state.basket!.itemCount -= 1;
                    }
                }
            });
    },
});

const handleFetchBasketItemsFulfilled = (
    state: BasketState,
    action: PayloadAction<ApplicationBasketItemList | null>
) => {
    if (action.payload) {
        if (state?.basketItems?.items) {
            state.basketItems.items = mergeArraysByField(state.basketItems.items, action.payload.items, "productId");
            state.basketItems.currentPosition = action.payload.currentPosition;
            state.basketItems.count = action.payload.count;
        } else {
            state.basketItems = action.payload;
        }
    }
};

const updateBasketItemProperty = <K extends keyof ApplicationBasketItem>(
    state: BasketState,
    basketItemId: number,
    property: K,
    value: ApplicationBasketItem[K]
) => {
    const basketItem = state.basketItems?.items.find(item => item.id === basketItemId);
    if (basketItem) {
        basketItem[property] = value;
    }
};

export const { resetBasket, updateApprovalStatus, resetBasketItems, resetBasketTotals } = basketSlice.actions;

export default basketSlice.reducer;
